import { uniqueId } from 'lodash';

import { Id, Key } from './types';

interface IdObject {
  id: Id;
  key?: Key;
}

export const compareIds = <T extends IdObject>(a: T, b: T): boolean => {
  return !!((a.id && a.id === b.id) || (a.key && a.key === b.key));
};

export const updateEntityByIndex = <T>(entities: T[] = [], entityIndex: number, updatedEntity: T): T[] => {
  if (entityIndex === -1) {
    return entities;
  } else {
    return [...entities.slice(0, entityIndex), updatedEntity, ...entities.slice(entityIndex + 1)];
  }
};

export const updateEntity = <T extends IdObject>(entities: T[] = [], updatedEntity: T): T[] => {
  const entityIndex = entities.findIndex(entity => compareIds(entity, updatedEntity));
  return updateEntityByIndex(entities, entityIndex, updatedEntity);
};

export const upsertEntityByIndex = <T>(entities: T[] = [], entityIndex: number, updatedEntity: T): T[] => {
  if (entityIndex === -1) {
    return [...entities, updatedEntity];
  } else {
    return [...entities.slice(0, entityIndex), updatedEntity, ...entities.slice(entityIndex + 1)];
  }
};

export const upsertEntity = <T extends IdObject>(entities: T[] = [], updatedEntity: T): T[] => {
  const entityIndex = entities.findIndex(entity => compareIds(entity, updatedEntity));
  return upsertEntityByIndex(entities, entityIndex, updatedEntity);
};

export const removeEntityByIndex = <T>(entities: T[] = [], entityIndex: number): T[] => {
  return [...entities.slice(0, entityIndex), ...entities.slice(entityIndex + 1)];
};

export const removeEntity = <T extends IdObject>(entities: T[] = [], removedEntity: T): T[] => {
  const entityIndex = entities.findIndex(entity => compareIds(entity, removedEntity));
  return removeEntityByIndex(entities, entityIndex);
};

export const removeKey = <T extends IdObject>(entity: T): T => {
  const updatedEntity = { ...entity };
  if (entity.key) {
    delete updatedEntity.key;
  }
  return updatedEntity;
};

export const removeKeys = <T extends IdObject>(entities: T[] = []): T[] => entities.map(removeKey);

export const addKey = <T extends IdObject>(entity: T): T => {
  if (entity.id > 0) {
    return entity;
  } else {
    return {
      ...entity,
      key: uniqueId(),
    };
  }
};

export const addKeys = <T extends IdObject>(entities: T[] = []): T[] => entities.map(addKey);

export const getIdOrKey = <T extends IdObject>(entity: T): Id | Key | undefined => entity.id || entity.key;

export const fillArray = (list: string[], length: number): string[] => (list.length ? list : new Array(length).fill(''));

export const fillArrayEnd = (list: string[], length: number): string[] => {
  while (list.length < length) {
    list.push('');
  }
  return list;
};
